/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import LogoDark from '../../images/logo/gitme-logo-white.svg';
import { validateEmail } from '../../utils';
import UserAgreementModal from '../../components/Payment/UserAgreementModal';
import Loader from '../../common/Loader';
import Loading from '../../components/Loading';
import { INTEGRATION_RETURN_PATH_KEY } from '../../utils/integrations';

const GetStartedGitlab = () => {
  const location = useLocation();

  const [userInfo, setUserInfo] = useState(null);
  const [email, setEmail] = useState();
  const [fullName, setFullName] = useState();
  const [companyName, setCompanyName] = useState();
  const [loading, setLoading] = useState();
  const [showSuccess, setShowSuccess] = useState(false);

  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
  const [openAcceptedAgreement, setOpenAcceptedAgreement] = useState(false);

  const fetchWithToken = async ({
    url,
    method = 'GET',
    data = null,
    onSuccess,
    onError
  }) => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) return;

    try {
      const res = await axios({
        url,
        method,
        data,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      onSuccess?.(res.data);
    } catch (err) {
      console.error('Request failed:', err);
      onError?.(err);
    }
  };

  useEffect(() => {
    fetchWithToken({
      url: `${process.env.REACT_APP_BACKEND_URL}/gitlab/get-data`,
      onSuccess: data => {
        const getData = data?.data;
        if (!getData) {
          toast.error('User not found');
          return;
        }
        const { existingUser } = getData;

        setEmail(existingUser.email);
        setFullName(existingUser.fullName);

        setUserInfo(getData);
      },
      onError: err =>
        toast.error(err?.response?.data?.message || 'Not found user')
    });
  }, [location]);

  useEffect(() => {
    const existingAuthToken = localStorage.getItem('authToken');
    const gitlabToken = new URLSearchParams(location.search).get('token');

    if (!existingAuthToken || !gitlabToken) {
      return;
    }

    setLoading(true);

    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/gitlab/lead-existing-user`,
        { gitlabToken },
        {
          headers: {
            Authorization: `Bearer ${existingAuthToken}`,
            'Content-Type': 'application/json'
          }
        }
      )
      .then(res => {
        if (res?.data?.message === 'SUCCESS') {
          const returnPath = localStorage.getItem(INTEGRATION_RETURN_PATH_KEY);
          if (returnPath) {
            localStorage.removeItem(INTEGRATION_RETURN_PATH_KEY);
            window.location.href = returnPath;
            return;
          }

          window.location.href = '/';
          return;
        }

        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to connect existing GitLab user', err);
        if (err?.response?.data?.error === 'SESSION_MISSING') {
          localStorage.removeItem('authToken');
        }
        setLoading(false);
      });
  }, [location.search]);

  const handleStart = async () => {
    setLoading(true);

    fetchWithToken({
      url: `${process.env.REACT_APP_BACKEND_URL}/gitlab/lead-register`,
      method: 'POST',
      data: {
        companyName,
        fullName,
        email
      },
      onSuccess: () => {
        setLoading(false);
        setShowSuccess(true);
      },
      onError: err => {
        setLoading(false);
        console.error('Failed to analyze repos', err);
        toast.error('Error: ' + err?.response?.data?.message);
      }
    });
  };

  const handleStarted = async () => {
    if (!hasAcceptedAgreement) {
      toast.error('Please accept the agreement to proceed.');
      return;
    }

    if (!companyName) {
      toast.error('Please select a company name to proceed.');
      return;
    }

    if (!fullName) {
      toast.error('Please enter your full name.');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    handleStart();
  };

  if (loading && !userInfo) {
    return <Loader />;
  }

  if (!userInfo) {
    return null;
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Left Side - Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-10 bg-white">
        {showSuccess ? (
          <div className=" left-6 z-50 rounded-xl  px-6 py-4 text-blue-800  w-full">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">✅</div>
              <div className="flex flex-col gap-5">
                <span className="font-semibold text-2xl">
                  Successfully submitted!
                </span>
                <span className="text-xl mt-1">
                  Your contact information has been received. We’ll be in touch
                  with you shortly.
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-md w-full">
            <h2 className="text-3xl font-bold mb-4 text-black">
              Let{"'"}s gets started for Gitlab
            </h2>
            {/* Company Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Enter your company"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
              />
            </div>
            {/* Full Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Enter your fullname"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
              />
            </div>
            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
              />
            </div>

            {/* Agreement */}
            <div className="pt-4">
              <label className="flex items-start text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-1 mr-2 accent-yellow-500"
                  onChange={e => {
                    setHasAcceptedAgreement(e.target.checked);
                    setOpenAcceptedAgreement(e.target.checked);
                  }}
                  checked={hasAcceptedAgreement}
                />
                <span>I have read and accept the agreement.</span>
              </label>
            </div>
            <UserAgreementModal
              isOpen={openAcceptedAgreement}
              onClose={() => {
                setOpenAcceptedAgreement(false);
              }}
            />
            {/* Button */}
            <button
              className="mt-6 w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
              onClick={handleStarted}
              disabled={loading}
            >
              {loading ? <Loading type="inline" /> : 'Get Started'}
            </button>
          </div>
        )}
      </div>

      {/* Right Side - Logo Only */}
      <div className="hidden md:flex w-1/2 bg-blue-600 items-center justify-center p-10 rounded-l-[2rem] flex-col text-center text-white">
        <img
          className="dark:block mb-6"
          src={LogoDark}
          alt="Logo"
          style={{ width: '10rem' }}
        />
        <p className="text-lg font-semibold mb-2 max-w-md">
          A New Standard in Developer Performance Analytics.
        </p>
        <p className="text-sm font-light max-w-md leading-relaxed">
          Gitme leverages advanced AI to deliver accurate, unified, and
          transparent insights — replacing fragmented metrics with a single
          source of truth.
        </p>
      </div>
    </div>
  );
};

export default GetStartedGitlab;
